import { Link } from "react-router-dom";
import type { Feature, Task } from "@/lib/api/types";
import { StatusDot } from "@/components/common/status-dot";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/lib/get-initials";
import { cn } from "@/lib/utils";
import { CircleDot, GitCommitVertical, CheckCircle2 } from "lucide-react";

type IssueItem =
  | { type: "feature"; data: Feature }
  | { type: "task"; data: Task };

interface IssueRowProps {
  item: IssueItem;
  className?: string;
  /** Whether this task is the last one under its parent feature */
  isLastTask?: boolean | undefined;
  /** Hide the status dot for features (used when grouped by status) */
  hideFeatureStatus?: boolean | undefined;
  /** When set, clicking opens inline instead of navigating */
  onSelect?: ((item: IssueItem) => void) | undefined;
  /** Currently selected item ID */
  isSelected?: boolean | undefined;
}

export function IssueRow({ item, className, isLastTask, hideFeatureStatus, onSelect, isSelected }: IssueRowProps) {
  const isFeature = item.type === "feature";
  const isTask = !isFeature;
  const data = item.data;

  // Tasks link to their parent feature (since there's no separate task detail page)
  const linkTo = isFeature
    ? `/features/${data.id}`
    : `/features/${(data as Task).featureId}`;
  const identifier = data.identifier;
  const title = data.title;
  const status = data.status;
  const assignee = data.assignee;

  // Check if item is completed or canceled
  const isCompleted = status?.category === "completed";
  const isCanceled = status?.category === "canceled";
  const isDone = isCompleted || isCanceled;

  // For features: show task count (completed/total)
  const taskCount = isFeature
    ? {
        total: (data as Feature)._count?.tasks ?? 0,
        completed: (data as Feature).completedTaskCount ?? 0,
      }
    : null;

  const rowClasses = cn(
    "flex items-center gap-3 px-4 py-2.5 transition-colors group cursor-pointer",
    isTask && "pl-8",
    isDone && "opacity-60",
    isSelected ? "bg-muted/60 border-l-2 border-primary" : "hover:bg-muted/30",
    className
  );

  const content = (
    <>
      {/* Tree connector for tasks */}
      {isTask && (
        <span className="flex items-center text-muted-foreground flex-shrink-0 -ml-4 w-4" aria-hidden="true">
          {isLastTask ? (
            <svg width="16" height="24" viewBox="0 0 16 24" fill="none" className="text-border">
              <path d="M8 0V12H16" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
          ) : (
            <svg width="16" height="24" viewBox="0 0 16 24" fill="none" className="text-border">
              <path d="M8 0V24M8 12H16" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
          )}
        </span>
      )}

      {/* Icon: Status dot for features, status-aware icon for tasks */}
      {isFeature ? (
        hideFeatureStatus ? null : status ? (
          <StatusDot status={status} />
        ) : (
          <CircleDot className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        )
      ) : isCompleted ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
      ) : (
        <GitCommitVertical className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 rotate-90" />
      )}

      {/* Identifier - narrower for tasks */}
      <span
        className={cn(
          "font-mono flex-shrink-0",
          isFeature
            ? "text-xs text-muted-foreground w-16"
            : "text-xs text-muted-foreground w-20"
        )}
      >
        {identifier}
      </span>

      {/* Title - muted for tasks, strikethrough for completed */}
      <span
        className={cn(
          "truncate text-sm",
          isFeature ? "font-medium" : "font-normal text-muted-foreground",
          isDone && "line-through"
        )}
      >
        {title}
      </span>

      {/* Task count for features - right after title */}
      {isFeature && taskCount && taskCount.total > 0 && (
        <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums">
          {taskCount.completed}/{taskCount.total}
        </span>
      )}

      {/* Spacer to push assignee to the right */}
      <span className="flex-1" />

      {/* Assignee avatar */}
      {assignee && (
        <Avatar className={cn("flex-shrink-0", isFeature ? "h-5 w-5" : "h-4 w-4")}>
          {assignee.avatarUrl && <AvatarImage src={assignee.avatarUrl} alt={assignee.name} />}
          <AvatarFallback className={cn(isFeature ? "text-xs" : "text-[10px]")}>
            {getInitials(assignee.name)}
          </AvatarFallback>
        </Avatar>
      )}
    </>
  );

  if (onSelect) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={rowClasses}
        onClick={() => onSelect(item)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(item); } }}
      >
        {content}
      </div>
    );
  }

  return (
    <Link to={linkTo} className={rowClasses}>
      {content}
    </Link>
  );
}
